"use client";

export type VizDatasetFieldMeta = {
  name: string;
  role: "x" | "y" | "series" | "dim" | "metric" | null;
  type: string;
  label: string | null;
  unit: string | null;
  lineInterpolation?: "linear" | "monotoneX" | null;
  sortable: boolean;
  filterable: boolean;
  exportable: boolean;
  defaultVisible: boolean;
  labelDomain: "ua.defaultChannelGroup" | null;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * row dataPath から対応する fields dataPath を導出する。
 *
 * @remarks
 * 可視化コンポーネントは rows/fields を別参照で読むため、
 * path 規約の変換を UI 側の 1 箇所に固定する。
 */
export function buildVizDatasetFieldsPath(dataPath: string): string {
  return dataPath.endsWith("/rows")
    ? `${dataPath.slice(0, -"/rows".length)}/fields`
    : `${dataPath}/fields`;
}

type FieldLike = {
  name?: unknown;
  role?: unknown;
  type?: unknown;
  label?: unknown;
  unit?: unknown;
  lineInterpolation?: unknown;
  sortable?: unknown;
  filterable?: unknown;
  exportable?: unknown;
  defaultVisible?: unknown;
  labelDomain?: unknown;
};

function parseVizDatasetFieldRole(value: unknown): VizDatasetFieldMeta["role"] {
  return value === "x" ||
    value === "y" ||
    value === "series" ||
    value === "dim" ||
    value === "metric"
    ? value
    : null;
}

function parseVizLineInterpolation(
  value: unknown,
): VizDatasetFieldMeta["lineInterpolation"] {
  return value === "linear" || value === "monotoneX" ? value : null;
}

/**
 * dataset fields payload を UI 参照用の最小メタへ正規化する。
 *
 * @remarks
 * 欠損や未知 shape は補完せず、描画側が安全に読める項目だけを残す。
 */
export function parseVizDatasetFields(value: unknown): VizDatasetFieldMeta[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => (isRecord(entry) ? (entry as FieldLike) : null))
    .filter((entry): entry is FieldLike => Boolean(entry))
    .map((entry) => ({
      name: typeof entry.name === "string" ? entry.name : "",
      role: parseVizDatasetFieldRole(entry.role),
      type: typeof entry.type === "string" ? entry.type : "",
      label:
        typeof entry.label === "string" && entry.label.trim().length > 0
          ? entry.label
          : null,
      unit:
        typeof entry.unit === "string" && entry.unit.trim().length > 0
          ? entry.unit
          : null,
      lineInterpolation: parseVizLineInterpolation(entry.lineInterpolation),
      sortable: entry.sortable !== false,
      filterable: entry.filterable === true,
      exportable: entry.exportable !== false,
      defaultVisible: entry.defaultVisible !== false,
      labelDomain:
        entry.labelDomain === "ua.defaultChannelGroup"
          ? ("ua.defaultChannelGroup" as const)
          : null,
    }))
    .filter((entry) => entry.name.length > 0);
}

/**
 * field name を key にした lookup map を作る。
 */
export function buildVizFieldMetaMap(
  fields: VizDatasetFieldMeta[],
): Map<string, VizDatasetFieldMeta> {
  return new Map(fields.map((field) => [field.name, field]));
}

/**
 * 指定 field が percent 表示対象かを判定する。
 *
 * @remarks
 * 推測ベースの suffix heuristic は使わず、adapter が付与した unit metadata のみを信頼する。
 */
export function isPercentFieldKey(
  key: string,
  metaByName: Map<string, VizDatasetFieldMeta>,
): boolean {
  return metaByName.get(key)?.unit === "percent";
}
